"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WhiteListStatusBadge = WhiteListStatusBadge;
exports.PaymentStatusBadge = PaymentStatusBadge;
exports.PaymentMethodBadge = PaymentMethodBadge;
exports.InvoiceTypeBadge = InvoiceTypeBadge;
const react_1 = __importDefault(require("react"));
const react_bootstrap_1 = require("react-bootstrap");
const CostInvoicesController_1 = require("./CostInvoicesController");
const ToolsDate_1 = __importDefault(require("../../React/Tools/ToolsDate"));
/**
 * NIP-K2 — badge statusu weryfikacji Białej Listy VAT (KAS wl-api) dla faktury
 * kosztowej. Mirrors the FidmanSyncBadge idiom (see
 * ../../View/Resultsets/CommonComponents.tsx): status → Badge + optional
 * tooltip with the check details (date + KAS request id).
 */
function WhiteListStatusBadge({ status, checkedAt, requestId, }) {
    const resolved = status || "NOT_CHECKED";
    const byStatus = {
        NOT_CHECKED: { bg: "secondary", text: "light", label: "Biała lista: nie sprawdzono" },
        VERIFIED_OK: { bg: "success", text: "light", label: "✅ Biała lista: OK" },
        VERIFIED_MISMATCH: { bg: "danger", text: "light", label: "⚠️ Biała lista: NIEZGODNOŚĆ" },
        ERROR: { bg: "warning", text: "dark", label: "⚠️ Biała lista: błąd sprawdzenia" },
        NOT_APPLICABLE: { bg: "secondary", text: "light", label: "Biała lista: nie dotyczy" },
    };
    const { bg, text, label } = byStatus[resolved];
    const badge = (react_1.default.createElement(react_bootstrap_1.Badge, { bg: bg, text: text }, label));
    if (!checkedAt && !requestId)
        return badge;
    return (react_1.default.createElement(react_bootstrap_1.OverlayTrigger, { placement: "top", overlay: react_1.default.createElement(react_bootstrap_1.Tooltip, { id: "white-list-tooltip" },
            checkedAt && react_1.default.createElement("div", null,
                "Sprawdzono: ",
                ToolsDate_1.default.dateYMDtoDMY(checkedAt)),
            requestId && react_1.default.createElement("div", null,
                "Nr wpisu KAS: ",
                requestId)) }, badge));
}
/**
 * Badge statusu płatności faktury kosztowej
 */
function PaymentStatusBadge({ status, paidAmount, grossAmount, }) {
    const resolved = status ?? CostInvoicesController_1.PaymentStatuses.UNPAID;
    let variant;
    let textVariant;
    let label;
    let icon;
    switch (resolved) {
        case CostInvoicesController_1.PaymentStatuses.PAID:
            variant = "success";
            textVariant = "light";
            label = "Zapłacona";
            icon = "✓";
            break;
        case CostInvoicesController_1.PaymentStatuses.NOT_APPLICABLE:
            variant = "info";
            textVariant = "light";
            label = "Nie dotyczy";
            icon = "–";
            break;
        case CostInvoicesController_1.PaymentStatuses.PARTIALLY_PAID: {
            const pct = grossAmount && paidAmount
                ? Math.round((paidAmount / grossAmount) * 100)
                : null;
            variant = "warning";
            textVariant = "dark";
            label = pct !== null ? `Częściowo ${pct}%` : "Częściowo";
            icon = "◑";
            break;
        }
        default:
            variant = "secondary";
            textVariant = "light";
            label = "Niezapłacona";
            icon = "●";
    }
    return (react_1.default.createElement(react_bootstrap_1.OverlayTrigger, { placement: "top", overlay: react_1.default.createElement(react_bootstrap_1.Tooltip, { id: "payment-status-tooltip" },
            "Status p\u0142atno\u015Bci: ",
            label,
            resolved === CostInvoicesController_1.PaymentStatuses.PARTIALLY_PAID && paidAmount !== undefined && grossAmount
                ? ` (${paidAmount.toFixed(2)} / ${grossAmount.toFixed(2)})`
                : "") },
        react_1.default.createElement(react_bootstrap_1.Badge, { bg: variant, text: textVariant },
            icon,
            " ",
            label)));
}
/**
 * Badge formy płatności wyciągniętej z KSeF.
 */
function PaymentMethodBadge({ paymentMethod }) {
    if (!paymentMethod) {
        return (react_1.default.createElement(react_bootstrap_1.Badge, { bg: "light", text: "dark", className: "border" }, "Brak formy platnosci"));
    }
    const normalized = normalizePaymentMethod(paymentMethod);
    const { icon, label, variant } = getPaymentMethodMeta(normalized, paymentMethod);
    return (react_1.default.createElement(react_bootstrap_1.OverlayTrigger, { placement: "top", overlay: react_1.default.createElement(react_bootstrap_1.Tooltip, { id: "payment-method-tooltip" },
            "Forma platnosci z KSeF: ",
            paymentMethod) },
        react_1.default.createElement(react_bootstrap_1.Badge, { bg: variant, text: variant === "warning" ? "dark" : "light" },
            icon,
            " ",
            label)));
}
function normalizePaymentMethod(paymentMethod) {
    return paymentMethod
        .trim()
        .toLocaleLowerCase("pl-PL")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
}
function getPaymentMethodMeta(normalized, rawValue) {
    if (normalized.includes("przelew")) {
        return { icon: "🏦", label: "Przelew", variant: "primary" };
    }
    if (normalized.includes("gotowka")) {
        return { icon: "💵", label: "Gotowka", variant: "success" };
    }
    if (normalized.includes("karta")) {
        return { icon: "💳", label: "Karta", variant: "info" };
    }
    if (normalized.includes("mobil")) {
        return { icon: "📱", label: "Mobilna", variant: "warning" };
    }
    if (normalized.includes("bon")) {
        return { icon: "🎟️", label: "Bon", variant: "secondary" };
    }
    if (normalized.includes("czek")) {
        return { icon: "🧾", label: "Czek", variant: "secondary" };
    }
    if (normalized.includes("kredyt")) {
        return { icon: "🏷️", label: "Kredyt", variant: "secondary" };
    }
    return {
        icon: "•",
        label: rawValue,
        variant: "secondary",
    };
}
/**
 * Mapowanie kodów KSeF → etykieta i wariant koloru.
 */
const INVOICE_TYPE_META = {
    "VAT": { label: "VAT", variant: "primary" },
    "KOR": { label: "Korekta", variant: "warning" },
    "ZAL": { label: "Zaliczka", variant: "info" },
    "ROZ": { label: "Rozliczenie", variant: "info" },
    "UPR": { label: "Uproszczona", variant: "secondary" },
    "KOR_ZAL": { label: "Kor. zaliczki", variant: "warning" },
    "KOR_ROZ": { label: "Kor. rozliczenia", variant: "warning" },
};
/**
 * Badge rodzaju faktury (RodzajFaktury z KSeF FA(3))
 */
function InvoiceTypeBadge({ invoiceType }) {
    if (!invoiceType)
        return null;
    const key = invoiceType.trim().toUpperCase();
    const meta = INVOICE_TYPE_META[key];
    const variant = meta?.variant ?? "secondary";
    const label = meta?.label ?? invoiceType;
    return (react_1.default.createElement(react_bootstrap_1.OverlayTrigger, { placement: "top", overlay: react_1.default.createElement(react_bootstrap_1.Tooltip, { id: "invoice-type-tooltip" },
            "Rodzaj faktury z KSeF: ",
            invoiceType) },
        react_1.default.createElement(react_bootstrap_1.Badge, { bg: variant, text: variant === "warning" ? "dark" : "light" }, label)));
}
